import { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { SHEET_TEMPLATES } from "@/lib/sheetTemplates";
import { ICON_BTN, BTN_GHOST } from "@/lib/styles";
import { X, Table2, Copy, Link2, Trash2, Plus, LayoutTemplate, FileSpreadsheet, Bookmark } from "lucide-react";
import { toast } from "sonner";

const MODAL_WRAP = "fixed inset-0 z-[75] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4";
const MODAL_BOX = "w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-2xl";

const TABS = [
  { key: "mine", label: "Mis hojas", Icon: FileSpreadsheet },
  { key: "templates", label: "Plantillas", Icon: LayoutTemplate },
  { key: "new", label: "Nueva", Icon: Plus },
];

export function SheetLibraryModal({ open, onClose, onInsertCopy, onInsertLinked, onCreateNew }) {
  const [tab, setTab] = useState("mine");
  const [sheets, setSheets] = useState([]);
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        api.get("/notes", { params: { type: "sheet" } }),
        api.get("/sheet-templates"),
      ]);
      setSheets(s.data);
      setSaved(t.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) { setTab("mine"); load(); } }, [open, load]);

  if (!open) return null;

  const insertCopyFromSheet = async (sheetNote) => {
    setBusyId(sheetNote.id);
    try {
      const { data } = await api.get(`/notes/${sheetNote.id}`);
      const cells = data.sheet?.cells;
      if (!Array.isArray(cells) || !cells.length) { toast.info("Esa hoja está vacía"); return; }
      onInsertCopy(cells);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusyId(null); }
  };

  const insertLinked = (sheetNote) => {
    onInsertLinked({ refId: sheetNote.id, title: sheetNote.title || "Hoja" });
    onClose();
  };

  const insertTemplate = (cells) => { onInsertCopy(cells); onClose(); };

  const deleteSaved = async (t) => {
    if (!window.confirm(`¿Eliminar la plantilla "${t.name}"?`)) return;
    try {
      await api.delete(`/sheet-templates/${t.id}`);
      setSaved((arr) => arr.filter((x) => x.id !== t.id));
      toast.success("Plantilla eliminada");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className={MODAL_WRAP} onClick={onClose} data-testid="sheet-library-modal">
      <div className={`${MODAL_BOX} max-w-lg`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-medium text-lg flex items-center gap-2"><Table2 size={18} className="text-emerald-500" /> Insertar hoja de cálculo</h3>
          <button onClick={onClose} className={ICON_BTN} data-testid="library-close"><X size={16} /></button>
        </div>

        <div className="flex items-center gap-1 mb-4 p-1 rounded-xl bg-slate-100 dark:bg-slate-800/60">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${tab === t.key ? "bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-sm" : "text-slate-500 dark:text-slate-400"}`}
              data-testid={`library-tab-${t.key}`}>
              <t.Icon size={14} /> {t.label}
            </button>
          ))}
        </div>

        <div className="max-h-[55vh] overflow-y-auto">
          {tab === "mine" && (
            loading ? <p className="font-mono2 text-sm text-slate-400 py-8 text-center">Cargando…</p> :
            sheets.length === 0 ? (
              <div className="text-center py-10 text-sm text-slate-500 dark:text-slate-400">
                Aún no tienes hojas de cálculo. Créalas desde el menú o en la pestaña "Nueva".
              </div>
            ) : (
              <div className="flex flex-col gap-2" data-testid="library-mine-list">
                {sheets.map((s) => (
                  <div key={s.id} className="flex items-center gap-2 p-3 rounded-xl border border-slate-200 dark:border-slate-800" data-testid={`library-sheet-${s.id}`}>
                    <FileSpreadsheet size={18} className="text-emerald-500 shrink-0" />
                    <span className="text-sm text-slate-700 dark:text-slate-200 truncate flex-1">{s.title || "Hoja de cálculo"}</span>
                    <button onClick={() => insertCopyFromSheet(s)} disabled={busyId === s.id} className={`${BTN_GHOST} text-xs py-1.5`} title="Insertar una copia independiente" data-testid={`insert-copy-${s.id}`}><Copy size={13} /> Copia</button>
                    <button onClick={() => insertLinked(s)} className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white transition-colors" title="Insertar vinculada — se sincroniza con la original" data-testid={`insert-linked-${s.id}`}><Link2 size={13} /> Vinculada</button>
                  </div>
                ))}
              </div>
            )
          )}

          {tab === "templates" && (
            <div className="flex flex-col gap-4">
              <div>
                <p className="text-[11px] uppercase tracking-wider font-mono2 text-slate-400 mb-2">Predefinidas</p>
                <div className="flex flex-col gap-2">
                  {SHEET_TEMPLATES.map((t) => (
                    <button key={t.id} onClick={() => insertTemplate(t.cells)} className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 dark:border-slate-800 hover:border-emerald-400 dark:hover:border-emerald-500/40 hover:bg-emerald-50/50 dark:hover:bg-emerald-500/5 transition-colors text-left" data-testid={`template-builtin-${t.id}`}>
                      <t.icon size={18} className="text-emerald-500 shrink-0" />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-slate-700 dark:text-slate-200 truncate">{t.name}</span>
                        <span className="block text-xs text-slate-400 truncate">{t.description}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wider font-mono2 text-slate-400 mb-2 flex items-center gap-1"><Bookmark size={11} /> Mis plantillas guardadas</p>
                {loading ? <p className="font-mono2 text-sm text-slate-400">Cargando…</p> :
                 saved.length === 0 ? (
                  <p className="text-xs text-slate-400 py-2">Aún no has guardado plantillas. Usa el ícono de marcador en una hoja para guardarla como plantilla.</p>
                 ) : (
                  <div className="flex flex-col gap-2" data-testid="library-saved-list">
                    {saved.map((t) => (
                      <div key={t.id} className="flex items-center gap-2 p-3 rounded-xl border border-slate-200 dark:border-slate-800" data-testid={`template-saved-${t.id}`}>
                        <Bookmark size={16} className="text-indigo-500 shrink-0" />
                        <span className="text-sm text-slate-700 dark:text-slate-200 truncate flex-1">{t.name}</span>
                        <button onClick={() => insertTemplate(t.cells)} className={`${BTN_GHOST} text-xs py-1.5`} data-testid={`insert-template-${t.id}`}><Copy size={13} /> Insertar</button>
                        <button onClick={() => deleteSaved(t)} className="text-slate-300 hover:text-rose-500 transition-colors" title="Eliminar" data-testid={`delete-template-${t.id}`}><Trash2 size={14} /></button>
                      </div>
                    ))}
                  </div>
                 )}
              </div>
            </div>
          )}

          {tab === "new" && (
            <div className="text-center py-8">
              <Plus size={32} className="mx-auto text-slate-300 dark:text-slate-600 mb-3" />
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">Crea una hoja desde cero con tus valores y fórmulas.</p>
              <button onClick={() => { onClose(); onCreateNew(); }} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium transition-colors" data-testid="library-create-new"><Plus size={16} /> Crear hoja desde cero</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
